import type { CompanyRole } from './api';
import type { Dictionary } from './i18n';

/**
 * Rodzaj gramatyczny odgadnięty z imienia i nazwiska.
 *
 * To jest **domysł**, nie wiedza — i dotyczy prawdziwej osoby, której nazwisko stoi obok.
 * Dlatego reguła jest ostrożna: zgadujemy tylko wtedy, gdy sygnał jest mocny, a przy
 * wątpliwości oddajemy `null` i ekran zostaje przy formie podstawowej. Lepiej napisać
 * „Księgowy" do kobiety o nazwisku, którego nie umiemy rozpoznać, niż odmieniać na chybił
 * trafił każde imię.
 *
 * Docelowo powinno to być **ustawienie konta** — człowiek sam wskazuje, jak się do niego
 * zwracać, a domysł jest tylko wartością początkową. Dopóki tego pola nie ma, działa sam domysł.
 */
export type Gender = 'F' | 'M';

/** Nazwiska przymiotnikowe: najpewniejszy sygnał w polszczyźnie, mocniejszy niż imię. */
const FEMININE_SURNAME = /(ska|cka|dzka|ża|owa|ówna)$/i;
const MASCULINE_SURNAME = /(ski|cki|dzki|ży)$/i;

/**
 * Męskie imiona zakończone na „a".
 *
 * Reguła „kończy się na a, więc kobieta" pada właśnie na nich — a Kuba czy Kosma to imiona,
 * które spotyka się na co dzień.
 */
const MALE_NAMES_IN_A = new Set([
  'kuba',
  'barnaba',
  'bonawentura',
  'kosma',
  'dyzma',
  'jarema',
  'zawisza',
  'sasza',
  'nikita',
  'ilia',
  'luca',
  'andrea',
  'nicola',
]);

/** Końcówki imion męskich, które nie kończą się spółgłoską. */
const MALE_NAME_ENDINGS = /(i|y|o)$/i;

export function detectGender(fullName: string | null | undefined): Gender | null {
  const words = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  // Nazwisko rozstrzyga pewniej niż imię: „Marcinkowska" to kobieta bez względu na to,
  // co stoi przed nim, a „-ski" nie bywa żeńskie
  const surname = words[words.length - 1];
  if (words.length > 1) {
    if (FEMININE_SURNAME.test(surname)) return 'F';
    if (MASCULINE_SURNAME.test(surname)) return 'M';
  }

  const first = words[0].toLowerCase();
  if (MALE_NAMES_IN_A.has(first)) return 'M';
  if (first.endsWith('a')) return 'F';
  if (MALE_NAME_ENDINGS.test(first)) return 'M';
  // Spółgłoska na końcu imienia: Sławomir, Piotr, Tomasz
  if (/[bcćdfghjklłmnńprsśtwzźż]$/i.test(first)) return 'M';
  return null;
}

/**
 * Nazwa roli dopasowana do osoby.
 *
 * Bez imienia i nazwiska (albo przy nierozpoznanym) zostaje forma podstawowa — ta sama, którą
 * widać w wykazie uprawnień, gdzie nie chodzi o nikogo konkretnego.
 */
export function roleLabel(t: Dictionary, role: CompanyRole | string, fullName?: string | null): string {
  const key = role as CompanyRole;
  const base = t.access.roles[key] ?? String(role);
  if (detectGender(fullName) !== 'F') return base;
  return t.access.rolesFemale[key] ?? base;
}
